import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { useCompare } from "../hooks/useCompare";
import TradenameAPI from "../api/tradename";
import { RiMedicineBottleLine, RiLeafLine, RiCloseLine } from "react-icons/ri";
import { FaRegBuilding, FaExternalLinkAlt } from "react-icons/fa";
import { HiOutlineBeaker } from "react-icons/hi";
import { MdCategory, MdCompareArrows } from "react-icons/md";
import { TbPill } from "react-icons/tb";

export default function Compare() {
  const [searchParams] = useSearchParams();
  const { compareItems, removeItem, clearAll } = useCompare();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get URIs from URL params or from context
  const itemUris = searchParams.get("items")?.split(",").filter(Boolean) || [];
  const urisToFetch =
    itemUris.length > 0
      ? itemUris
      : compareItems.map((item) => item.uri);

  useEffect(() => {
    const fetchProducts = async () => {
      if (urisToFetch.length === 0) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const results = await Promise.all(
          urisToFetch.map((uri) => TradenameAPI.getOne(uri))
        );
        setProducts(results.filter(Boolean));
      } catch (err) {
        setError("Failed to load product details for comparison.");
        console.error("Error fetching comparison products:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [urisToFetch.join(",")]);

  if (isLoading) {
    return (
      <PageLayout
        title="Compare"
        titleHighlight="Medicines"
        subtitle="Loading comparison data"
        pageTitle="Compare Medicines"
        showSearch={false}
        showGradient={true}
      >
        <ComparisonSkeleton count={urisToFetch.length || 2} />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        title="Compare"
        titleHighlight="Medicines"
        subtitle="Side-by-side comparison"
        pageTitle="Compare Medicines"
        showSearch={false}
        showGradient={true}
      >
        <div className="p-6 text-center border shadow-sm bg-white/20 backdrop-blur-sm border-white/40 rounded-xl">
          <div className="mb-4 text-red-500">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-primary hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </PageLayout>
    );
  }

  if (products.length === 0) {
    return (
      <PageLayout
        title="Compare"
        titleHighlight="Medicines"
        subtitle="Select medicines to compare"
        pageTitle="Compare Medicines"
        showSearch={false}
        showGradient={true}
      >
        <EmptyComparison />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Compare"
      titleHighlight="Medicines"
      subtitle={`Comparing ${products.length} products side by side`}
      pageTitle="Compare Medicines"
      showSearch={false}
      showGradient={true}
    >
      <div className="mt-6">
        {/* Desktop: Table view */}
        <div className="hidden md:block">
          <ComparisonTable products={products} />
        </div>

        {/* Mobile: Swipeable cards */}
        <div className="md:hidden">
          <ComparisonMobile products={products} />
        </div>
      </div>
    </PageLayout>
  );
}

/* ─── Comparison Table (Desktop) ─────────────────────────────────────────── */

function ComparisonTable({ products }) {
  // Collect all unique ingredients across products
  const allIngredients = new Set();
  products.forEach((p) =>
    p.ingredients?.forEach((ing) => allIngredients.add(ing.name))
  );

  // Collect all unique categories
  const allCategories = new Set();
  products.forEach((p) =>
    p.categories?.forEach((cat) => allCategories.add(cat))
  );

  return (
    <div className="overflow-hidden border rounded-xl bg-white/60 border-white/60">
      <div className="overflow-x-auto">
        <table className="w-full">
          {/* Header: Product names */}
          <thead>
            <tr className="border-b border-secondary/8">
              <th className="p-4 text-left text-14 font-text font-medium text-secondary/80 w-[180px] min-w-[180px] align-top bg-white/40">
                Product
              </th>
              {products.map((product) => (
                <th
                  key={product.uri}
                  className="p-4 text-left align-top min-w-[200px]"
                >
                  <Link
                    to={`/tradenames/${product.uri}`}
                    className="group inline-flex items-start gap-2"
                  >
                    <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10 flex-shrink-0">
                      <RiMedicineBottleLine className="size-4 text-primary" />
                    </div>
                    <div>
                      <span className="text-16 font-semibold text-secondary group-hover:text-primary transition-colors block">
                        {product.name}
                      </span>
                      <span className="text-14 font-text text-secondary/70 mt-0.5 block">
                        {product.manufacturers?.[0]?.name || "Unknown"}
                      </span>
                    </div>
                  </Link>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/* Manufacturer */}
            <ComparisonRow
              label="Manufacturer"
              icon={FaRegBuilding}
              products={products}
              renderCell={(product) => (
                <div className="space-y-1">
                  {product.manufacturers?.map((m, i) => (
                    <Link
                      key={i}
                      to={`/manufacturers/${m.uri || m.name}`}
                      className="block text-14 font-text text-secondary hover:text-primary transition-colors"
                    >
                      {m.name}
                    </Link>
                  )) || (
                    <span className="text-14 font-text text-secondary/70">
                      Not available
                    </span>
                  )}
                </div>
              )}
            />

            {/* Active Ingredients */}
            <ComparisonRow
              label="Active Ingredients"
              icon={RiLeafLine}
              products={products}
              renderCell={(product) => (
                <div className="flex flex-wrap gap-1.5">
                  {product.ingredients?.map((ing) => {
                    // Check if this ingredient is shared across all products
                    const isShared = products.every((p) =>
                      p.ingredients?.some((i) => i.name === ing.name)
                    );
                    return (
                      <Link
                        key={ing.uri}
                        to={`/ingredients/${ing.uri}`}
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-12 font-text transition-colors ${
                          isShared
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-secondary/5 text-secondary/70 hover:bg-secondary/10"
                        }`}
                      >
                        {ing.name}
                      </Link>
                    );
                  }) || (
                    <span className="text-14 font-text text-secondary/70">
                      Not available
                    </span>
                  )}
                </div>
              )}
            />

            {/* Dosage Forms */}
            <ComparisonRow
              label="Dosage Forms"
              icon={TbPill}
              products={products}
              renderCell={(product) => (
                <div className="space-y-1.5">
                  {product.forms?.length > 0 ? (
                    product.forms.map((form, i) => (
                      <div key={i} className="text-14 font-text text-secondary">
                        <span className="font-medium">{form[0]}</span>
                        {form[1] && (
                          <span className="text-secondary/80 ml-1">
                            ({form[1]})
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <span className="text-14 font-text text-secondary/70">
                      Not available
                    </span>
                  )}
                </div>
              )}
            />

            {/* Composition */}
            <ComparisonRow
              label="Composition"
              icon={HiOutlineBeaker}
              products={products}
              renderCell={(product) => (
                <div className="space-y-2">
                  {product.forms?.some((f) => f.length > 2) ? (
                    product.forms.map(
                      (form, i) =>
                        form.length > 2 && (
                          <div key={i}>
                            <span className="text-12 font-text font-medium text-secondary/70 block mb-1">
                              {form[0]}:
                            </span>
                            <ul className="space-y-0.5">
                              {form.slice(2).map((ingredient, j) => (
                                <li
                                  key={j}
                                  className="text-14 font-text text-secondary/70 pl-3 relative before:content-['·'] before:absolute before:left-0 before:text-primary"
                                >
                                  {ingredient}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                    )
                  ) : (
                    <span className="text-14 font-text text-secondary/70">
                      Not available
                    </span>
                  )}
                </div>
              )}
            />

            {/* Categories */}
            <ComparisonRow
              label="Categories"
              icon={MdCategory}
              products={products}
              renderCell={(product) => (
                <div className="flex flex-wrap gap-1.5">
                  {product.categories?.length > 0 ? (
                    product.categories.map((cat, i) => {
                      const isShared = products.every((p) =>
                        p.categories?.includes(cat)
                      );
                      return (
                        <span
                          key={i}
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-12 font-text ${
                            isShared
                              ? "bg-primary/8 text-primary"
                              : "bg-secondary/5 text-secondary/70"
                          }`}
                        >
                          {cat}
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-14 font-text text-secondary/70">
                      Not available
                    </span>
                  )}
                </div>
              )}
            />

            {/* Where to buy */}
            <ComparisonRow
              label="Where to Buy"
              icon={FaExternalLinkAlt}
              products={products}
              renderCell={(product) => (
                <Link
                  to={`https://pharmacypilot.info?utm_source=pharmacywiki&utm_medium=compare&utm_campaign=${product.uri}`}
                  target="_blank"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-12 font-text font-medium text-white bg-green-500 hover:bg-green-600 rounded-md transition-colors"
                >
                  <FaExternalLinkAlt className="size-3" />
                  PharmacyPilot
                </Link>
              )}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Comparison Row ─────────────────────────────────────────────────────── */

function ComparisonRow({ label, icon: Icon, products, renderCell }) {
  return (
    <tr className="border-b border-secondary/5 last:border-b-0">
      <td className="p-4 align-top bg-white/40">
        <div className="flex items-center gap-2">
          <Icon className="size-4 text-primary/70 flex-shrink-0" />
          <span className="text-14 font-text font-medium text-secondary/80">
            {label}
          </span>
        </div>
      </td>
      {products.map((product) => (
        <td key={product.uri} className="p-4 align-top">
          {renderCell(product)}
        </td>
      ))}
    </tr>
  );
}

/* ─── Mobile Comparison ──────────────────────────────────────────────────── */

function ComparisonMobile({ products }) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="space-y-4">
      {/* Product selector tabs */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
        {products.map((product, i) => (
          <button
            key={product.uri}
            onClick={() => setActiveIndex(i)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-12 font-text font-medium flex-shrink-0 transition-colors ${
              activeIndex === i
                ? "bg-primary text-white"
                : "bg-white border border-secondary/10 text-secondary/70"
            }`}
          >
            <RiMedicineBottleLine className="size-3.5" />
            <span className="max-w-[100px] truncate">{product.name}</span>
          </button>
        ))}
      </div>

      {/* Active product details */}
      <div className="space-y-3">
        {products[activeIndex] && (
          <MobileProductCard product={products[activeIndex]} allProducts={products} />
        )}
      </div>

      {/* Swipe hint */}
      {products.length > 1 && (
        <p className="text-center text-14 font-text text-secondary/70">
          Tap tabs above to switch between products
        </p>
      )}
    </div>
  );
}

function MobileProductCard({ product, allProducts }) {
  const sections = [
    {
      label: "Manufacturer",
      icon: FaRegBuilding,
      content: product.manufacturers?.map((m) => m.name).join(", ") || "Not available",
    },
    {
      label: "Active Ingredients",
      icon: RiLeafLine,
      content: product.ingredients?.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {product.ingredients.map((ing) => {
            const isShared = allProducts.every((p) =>
              p.ingredients?.some((i) => i.name === ing.name)
            );
            return (
              <Link
                key={ing.uri}
                to={`/ingredients/${ing.uri}`}
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-12 font-text ${
                  isShared
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-secondary/5 text-secondary/70"
                }`}
              >
                {ing.name}
              </Link>
            );
          })}
        </div>
      ) : "Not available",
    },
    {
      label: "Dosage Forms",
      icon: TbPill,
      content: product.forms?.length > 0
        ? product.forms.map((f) => f[0]).join(", ")
        : "Not available",
    },
    {
      label: "Composition",
      icon: HiOutlineBeaker,
      content: product.forms?.some((f) => f.length > 2) ? (
        <div className="space-y-1.5">
          {product.forms.map(
            (form, i) =>
              form.length > 2 && (
                <div key={i}>
                  <span className="text-12 font-text font-medium text-secondary/70">
                    {form[0]}:
                  </span>
                  <span className="text-14 font-text text-secondary/80 ml-1">
                    {form.slice(2).join(", ")}
                  </span>
                </div>
              )
          )}
        </div>
      ) : "Not available",
    },
    {
      label: "Categories",
      icon: MdCategory,
      content: product.categories?.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {product.categories.map((cat, i) => {
            const isShared = allProducts.every((p) =>
              p.categories?.includes(cat)
            );
            return (
              <span
                key={i}
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-12 font-text ${
                  isShared
                    ? "bg-primary/8 text-primary"
                    : "bg-secondary/5 text-secondary/70"
                }`}
              >
                {cat}
              </span>
            );
          })}
        </div>
      ) : "Not available",
    },
  ];

  return (
    <div className="border rounded-xl bg-white/60 border-white/60 overflow-hidden">
      {/* Product header */}
      <div className="p-4 border-b border-secondary/5 bg-white/40">
        <Link
          to={`/tradenames/${product.uri}`}
          className="group flex items-center gap-3"
        >
          <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10">
            <RiMedicineBottleLine className="size-5 text-primary" />
          </div>
          <div>
            <h3 className="text-16 font-semibold text-secondary group-hover:text-primary transition-colors">
              {product.name}
            </h3>
            <p className="text-14 font-text text-secondary/70">
              {product.manufacturers?.[0]?.name || "Unknown manufacturer"}
            </p>
          </div>
        </Link>
      </div>

      {/* Sections */}
      <div className="divide-y divide-secondary/5">
        {sections.map((section) => (
          <div key={section.label} className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <section.icon className="size-3.5 text-primary/70" />
              <span className="text-12 font-text font-medium text-secondary/80">
                {section.label}
              </span>
            </div>
            <div className="text-14 font-text text-secondary">
              {typeof section.content === "string" ? (
                <span className={section.content === "Not available" ? "text-secondary/70" : ""}>
                  {section.content}
                </span>
              ) : (
                section.content
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Buy link */}
      <div className="p-4 border-t border-secondary/5 bg-white/40">
        <Link
          to={`https://pharmacypilot.info?utm_source=pharmacywiki&utm_medium=compare&utm_campaign=${product.uri}`}
          target="_blank"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-14 font-text font-medium text-white bg-green-500 hover:bg-green-600 rounded-lg transition-colors w-full justify-center"
        >
          <FaExternalLinkAlt className="size-3.5" />
          Find at PharmacyPilot
        </Link>
      </div>
    </div>
  );
}

/* ─── Empty State ────────────────────────────────────────────────────────── */

function EmptyComparison() {
  return (
    <div className="py-16 text-center">
      <div className="inline-flex items-center justify-center size-16 rounded-2xl bg-primary/8 mb-4">
        <MdCompareArrows className="size-8 text-primary/60" />
      </div>
      <h3 className="text-18 font-semibold text-secondary mb-2">
        No medicines selected
      </h3>
      <p className="text-14 font-text text-secondary/80 max-w-md mx-auto mb-6">
        Browse trade names and tap the compare checkbox on any product card to
        add it here. You can compare up to 4 medicines side by side.
      </p>
      <Link
        to="/tradenames"
        className="inline-flex items-center gap-2 px-5 py-2.5 text-14 font-text font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
      >
        <RiMedicineBottleLine className="size-4" />
        Browse Trade Names
      </Link>
    </div>
  );
}

/* ─── Skeleton ───────────────────────────────────────────────────────────── */

function ComparisonSkeleton({ count }) {
  return (
    <div className="mt-6 border rounded-xl bg-white/60 border-white/60 overflow-hidden animate-pulse">
      <div className="p-4 border-b border-secondary/5">
        <div className="flex gap-4">
          <div className="w-[180px]" />
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-lg bg-secondary/8" />
                <div>
                  <div className="h-4 w-24 rounded bg-secondary/8 mb-1" />
                  <div className="h-3 w-16 rounded bg-secondary/6" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="p-4 border-b border-secondary/5 last:border-b-0">
          <div className="flex gap-4">
            <div className="w-[180px]">
              <div className="h-4 w-28 rounded bg-secondary/6" />
            </div>
            {Array.from({ length: count }).map((_, j) => (
              <div key={j} className="flex-1 min-w-[200px]">
                <div className="h-4 w-32 rounded bg-secondary/6 mb-2" />
                <div className="h-3 w-20 rounded bg-secondary/5" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
